import type { HomeCalendarEvent } from '../../../services/home/types'
import type { SnEvent } from '../types'

/**
 * A competition reference as the calendar feed may send it.
 *
 * The feed currently sends a bare NAME string where the event endpoints send a
 * `{ id, name }` relation. Both are accepted here so the day the backend
 * switches to the relation shape, this file needs no edit.
 */
type CalendarSchedule = HomeCalendarEvent['schedule']

/**
 * Marks a competition whose id the feed did not send.
 *
 * `resolveEventCompetition` returns a competition only when it has BOTH an id
 * and a name, so a name-only value needs a stand-in id to render at all. This
 * sentinel is that stand-in: it is deliberately not a plausible id, so any
 * code that ever tries to ROUTE on it fails loudly rather than silently
 * navigating to a competition that does not exist.
 *
 * Safe on the surfaces this mapper feeds — the home widget's card only prints
 * `competition.name`, and the card taps through to the EVENT, not the
 * competition. The detail screens, which do read the id, load their event from
 * `/events/:id`, whose schedule is a real relation and never reaches here.
 */
export const UNRESOLVED_SCHEDULE_ID = '__unresolved__'

/**
 * Normalizes the feed's competition field to the relation shape `SnEvent`
 * declares.
 *
 * A bare string is carried through as a NAME under the sentinel id above, so
 * the competition line shows in the layout. Once the backend sends the
 * relation shape, the real id flows through instead and nothing here changes.
 */
const toScheduleRef = (
  schedule: CalendarSchedule
): { id: string; name?: string } | null => {
  if (!schedule) {
    return null
  }

  if (typeof schedule === 'string') {
    const name = schedule.trim()
    return name ? { id: UNRESOLVED_SCHEDULE_ID, name } : null
  }

  const id = schedule.id?.trim()
  const name = schedule.name?.trim()
  if (!id) {
    return null
  }
  return name ? { id, name } : { id }
}

/**
 * Adapts one `/home/calendar` event onto the `SnEvent` shape the shared event
 * components read.
 *
 * This exists so `EventCard` — and the badges, time formatting and resolvers
 * behind it — can be reused by the home widget completely unchanged. The two
 * shapes agree on the fields that matter most (`id`, `name`, `details`,
 * `starts_at`, `timezone`, `category`, `cancelled`, `team`), which are carried
 * straight through. Two fields differ:
 *
 * - **Location.** The feed nests a `{ name, address, timezone }` object;
 *   `SnEvent` carries a flat `address_name` plus a `venue`. The venue's
 *   timezone is mapped through as well, so `resolveEventTimezone` still finds
 *   a zone when the event omits its own — without it the card would silently
 *   attribute a venue-anchored event to the viewer's clock.
 * - **Competition.** String vs relation — see `toScheduleRef`.
 */
export const toSnEvent = (event: HomeCalendarEvent): SnEvent => {
  // The feed carries a location in more than one place, and which one is
  // populated varies per item: the flattened `location` object, and/or the
  // venue/address_name pair the event endpoints send. Reading only `location`
  // dropped the name for every item that carried `address_name` instead.
  const venueName = event.venue?.name?.trim() || null
  const addressName = event.address_name?.trim() || null
  const feedLocationName = event.location?.name?.trim() || null
  const locationTimezone =
    event.venue?.timezone?.trim() || event.location?.timezone?.trim() || null
  // Venue wins, matching `formatEventLocation`'s own precedence; the flattened
  // name is the last resort.
  const resolvedVenueName = venueName || feedLocationName

  return {
    id: event.id,
    name: event.name,
    details: event.details ?? null,
    category: event.category ?? null,
    starts_at: event.starts_at,
    ends_at: event.ends_at ?? null,
    timezone: event.timezone ?? null,
    arrival_time_minutes: event.arrival_time_minutes ?? null,
    cancelled: Boolean(event.cancelled),
    event_type: event.event_type ?? null,
    // Kept as SEPARATE fields rather than collapsed into one, so
    // `formatEventLocation` applies its normal precedence (venue → facility →
    // address_name → street) instead of this mapper second-guessing it.
    address_name: addressName,
    // Carries the zone as well as the name, so `resolveEventTimezone` can fall
    // back to it when the event omits its own — without that a venue-anchored
    // event renders silently on the viewer's clock. Null when the feed sent
    // neither, rather than an empty object.
    venue:
      resolvedVenueName || locationTimezone
        ? { name: resolvedVenueName, timezone: locationTimezone }
        : null,
    team: event.team
      ? {
          id: event.team.id,
          name: event.team.name,
          // The feed sends a URL STRING; `SnEvent` declares the image
          // object the event endpoints return. Carried as `full_path`, which
          // is the key `resolveAssetUrl` reads first — so the card resolves it
          // through the CDN rewrite exactly as it does elsewhere.
          image: event.team.image ? { full_path: event.team.image } : null,
        }
      : null,
    schedule: toScheduleRef(event.schedule),
    root_schedule: toScheduleRef(event.root_schedule),
  }
}

/** Maps a whole feed array. */
export const toSnEvents = (events: HomeCalendarEvent[]): SnEvent[] =>
  events.map(toSnEvent)
